"use client";

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "~/components/ui/card";
import { buttonVariants } from "~/components/ui/button";
import {
  CalendarIcon,
  HeartHandshakeIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from "lucide-react";
import Link from "next/link";
import { api } from "~/trpc/react";
import { useParams, useRouter } from "next/navigation";
import { useCountdownFromNow } from "~/hooks/use-countdown";
import { useCallback, useEffect, useState } from "react";
import Confetti from "react-confetti";
import { TherapyRelationshipType } from "~/prisma/browser";

interface SessionDetails {
  therapistName: string;
  therapistId: string;
  therapyType: TherapyRelationshipType;
  sessionDate: string;
  sessionTime: string;
  relationshipId?: string | null;
  isLogged?: boolean;
}

export function PaymentSuccessfulComponent(sessionDetails: SessionDetails) {
  const [isClient, setIsClient] = useState(false);
  const [startCountdown, setStartCountdown] = useState(false);
  const [allowFallbackRedirect, setAllowFallbackRedirect] = useState(false);
  const utils = api.useUtils();

  useEffect(() => {
    if (!sessionDetails.isLogged) {
      return;
    }

    void utils.therapySessionRouter.getTherapyRelationships.invalidate();
  }, [sessionDetails.isLogged, utils]);

  const resolvedRelationship =
    api.therapySessionRouter.getTherapyRelationshipByTherapist.useQuery(
      {
        therapistId: sessionDetails.therapistId,
        therapyType: sessionDetails.therapyType,
      },
      {
        enabled: Boolean(
          sessionDetails.isLogged && !sessionDetails.relationshipId,
        ),
        refetchInterval: (query) => (query.state.data ? false : 1000),
      },
    );

  const therapyRelationships =
    api.therapySessionRouter.getTherapyRelationships.useQuery(undefined, {
      enabled: Boolean(
        sessionDetails.isLogged && !sessionDetails.relationshipId,
      ),
    });

  const { therapistSlug } = useParams<{ therapistSlug: string }>();

  const currentTherapyRelationsip = therapyRelationships.data?.find(
    (relationship) =>
      relationship.therapist.slug === therapistSlug &&
      relationship.type === sessionDetails.therapyType,
  );

  const router = useRouter();

  // Prevent hydration mismatch by only rendering confetti on client
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Start countdown after component is fully mounted and visible
  useEffect(() => {
    const timer = setTimeout(() => setStartCountdown(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!startCountdown) {
      return;
    }

    const timer = setTimeout(() => setAllowFallbackRedirect(true), 10000);
    return () => clearTimeout(timer);
  }, [startCountdown]);

  const countdown = useCountdownFromNow(5);

  const getUrl = useCallback(() => {
    if (sessionDetails.relationshipId) {
      return `/app/inbox/${sessionDetails.relationshipId}`;
    }

    if (resolvedRelationship.data?.id) {
      return `/app/inbox/${resolvedRelationship.data.id}`;
    }

    if (currentTherapyRelationsip) {
      return `/app/inbox/${currentTherapyRelationsip.id}`;
    }

    return "/app/inbox";
  }, [
    currentTherapyRelationsip,
    resolvedRelationship.data?.id,
    sessionDetails.relationshipId,
  ]);

  // Redirect when countdown expires (only if countdown has started)
  useEffect(() => {
    if (!startCountdown || !countdown.isExpired) {
      return;
    }

    if (
      resolvedRelationship.data?.id ||
      sessionDetails.relationshipId ||
      currentTherapyRelationsip ||
      !sessionDetails.isLogged ||
      resolvedRelationship.isError ||
      allowFallbackRedirect
    ) {
      router.push(getUrl());
    }
  }, [
    startCountdown,
    countdown.isExpired,
    currentTherapyRelationsip,
    getUrl,
    resolvedRelationship.data?.id,
    resolvedRelationship.isError,
    router,
    allowFallbackRedirect,
    sessionDetails.isLogged,
    sessionDetails.relationshipId,
  ]);

  return (
    <div className="flex h-full items-start justify-center bg-linear-to-br from-green-50 to-blue-50 px-8 py-28">
      {isClient && <Confetti recycle={false} />}
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1">
          <div className="mb-4 flex justify-center">
            <CheckCircleIcon className="h-16 w-16 text-green-500" />
          </div>
          <CardTitle className="text-brand text-center text-2xl">
            Uspešno ste zakazali sesiju
          </CardTitle>
          <CardDescription className="text-center">
            Hvala što koristite našu platformu
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3 rounded-lg bg-green-100 p-3">
              <HeartHandshakeIcon className="text-brand h-6 w-6" />
              <div>
                <p className="text-sm text-muted-foreground">Terapeut</p>
                <p className="font-semibold">{sessionDetails.therapistName}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3 rounded-lg bg-purple-100 p-3">
              <CalendarIcon className="h-6 w-6 text-purple-600" />
              <div>
                <p className="text-sm text-muted-foreground">Datum</p>
                <p className="font-semibold">
                  {sessionDetails.sessionDate}, {sessionDetails.sessionTime}h
                </p>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col">
          <Link
            href={getUrl()}
            className={buttonVariants({
              variant: "default",
              className: "w-full",
            })}
          >
            Razgovor sa terapeutom
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Link>
          {startCountdown && (
            <p className="mt-2 text-muted-foreground">
              redirekt za {Math.max(countdown.seconds, 0)} sekundi
            </p>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
